import { FC } from 'react';

import dayjs from 'dayjs';
import { Formik, FormikHelpers } from 'formik';
import useSWR from 'swr';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers';

import { DailyTaskFormValues, dailyTaskValidationSchema, DESCRIPTION_MAX_LENGTH } from './form';
import { TournamentSlotPicker } from './tournamentSlotPicker/TournamentSlotPicker';
import { DEFAULT_REWARD_STUB } from './utils';

import { ticketService } from 'v2/api/ticketService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RewardsFormControl } from 'v2/components/form/rewardsFormControl/RewardsFormControl';
import { Select } from 'v2/components/select/Select';
import { TournamentSelect } from 'v2/components/select/tournamentSelect/TournamentSelect';
import { DailyTaskType, SpecialTaskType, USER_ROLES } from 'v2/constants';
import { DailyRewardAvailableTickets } from 'v2/pages/dailyReward/DailyRewardForm/DailyRewardAvailableTickets';
import { SelectOption } from 'v2/types';

interface DailyTaskFormProps {
  initialValues: DailyTaskFormValues;
  isEditForm?: boolean;
  submitButtonCaption?: string;
  isSubmitting?: boolean;
  onSubmit: (values: DailyTaskFormValues, helpers: FormikHelpers<DailyTaskFormValues>) => void | Promise<void>;
}

const taskTypeOptions: SelectOption<DailyTaskType>[] = Object.values(DailyTaskType).map((type) => ({
  id: type,
  value: type,
  label: type,
}));

const specialTaskTypeOptions: SelectOption<SpecialTaskType>[] = Object.values(SpecialTaskType).map((type) => ({
  id: type,
  value: type,
  label: type,
}));

export const DailyTaskForm: FC<DailyTaskFormProps> = ({
  initialValues,
  isEditForm = false,
  submitButtonCaption = 'Save',
  isSubmitting = false,
  onSubmit,
}) => {
  const { data: unassignedTickets = [] } = useSWR('getUnassignedTickets', ticketService.getUnassignedTickets);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={dailyTaskValidationSchema}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur
    >
      {(formik) => {
        const { values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, dirty } = formik;

        const showError = (field: keyof DailyTaskFormValues) => Boolean(touched[field] && errors[field]);
        const errorText = (field: keyof DailyTaskFormValues) =>
          touched[field] && typeof errors[field] === 'string' ? (errors[field] as string) : undefined;

        const numberField = (field: keyof DailyTaskFormValues, label: string) => (
          <TextField
            name={field}
            label={label}
            type="number"
            value={values[field] as number}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showError(field)}
            helperText={errorText(field)}
            fullWidth
          />
        );

        const goldenTicketBaseOptions: SelectOption[] = unassignedTickets.map((ticket) => ({
          id: ticket.id,
          value: ticket.id,
          label: `${dayjs(ticket.date).format('YYYY-MM-DD')} ${ticket.description}`,
        }));

        const currentGoldenTicketId = initialValues.goldenTicketId;
        const goldenTicketOptions =
          currentGoldenTicketId && !goldenTicketBaseOptions.some((option) => option.value === currentGoldenTicketId)
            ? [
                { id: currentGoldenTicketId, value: currentGoldenTicketId, label: currentGoldenTicketId },
                ...goldenTicketBaseOptions,
              ]
            : goldenTicketBaseOptions;

        const renderDefaultFields = () => {
          switch (values.type) {
            case DailyTaskType.X:
              return (
                <>
                  {numberField('taskX', 'Task X Value')}
                  <FormControlLabel
                    control={<Checkbox name="isBonusBuy" checked={values.isBonusBuy} onChange={handleChange} />}
                    label="Bonus Buy"
                  />
                  {numberField('betLimit', 'Bet Limit')}
                </>
              );
            case DailyTaskType.SUM_X:
              return (
                <>
                  {numberField('lowerLimitX', 'Lower limit X')}
                  {numberField('betLimit', 'Bet Limit')}
                  {numberField('totalTaskX', 'Total task X')}
                </>
              );
            case DailyTaskType.COUNT_HIT_X:
              return (
                <>
                  {numberField('taskX', 'Task X Value')}
                  {numberField('betLimit', 'Bet Limit')}
                  {numberField('countTaskX', 'Count Task X')}
                </>
              );
            case DailyTaskType.SPECIFIC_X:
              return (
                <>
                  {numberField('betLimit', 'Bet Limit')}
                  {numberField('lowerRangeX', 'Lower range X')}
                  {numberField('upperRangeX', 'Upper range X')}
                </>
              );
            case DailyTaskType.TOP_X:
              return (
                <>
                  <DateTimePicker
                    label="End date *"
                    value={values.endDate}
                    onChange={(value) => setFieldValue('endDate', value)}
                    format="DD.MM.YYYY HH:mm"
                  />
                  {numberField('betLimit', 'Bet Limit')}
                </>
              );
            default:
              return null;
          }
        };

        const renderSpecialFields = () => {
          switch (values.specialTaskType) {
            case SpecialTaskType.TASKS:
              return numberField('tasksToUnlock', 'Count tasks to unlock Epic');
            case SpecialTaskType.REFERRALS:
              return numberField('referralsToUnlock', 'Count referrals to unlock Epic');
            case SpecialTaskType.PASSWORD:
              return (
                <TextField
                  name="passwordToUnlock"
                  label="Password to unlock Epic"
                  value={values.passwordToUnlock}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={showError('passwordToUnlock')}
                  helperText={errorText('passwordToUnlock')}
                  fullWidth
                />
              );
            case SpecialTaskType.GOLDEN_TICKET:
              return (
                <Select
                  label="Golden Ticket"
                  value={values.goldenTicketId}
                  options={goldenTicketOptions}
                  onChange={(value) => setFieldValue('goldenTicketId', value)}
                  required
                />
              );
            default:
              return null;
          }
        };

        return (
          <form className="form" onSubmit={handleSubmit}>
            <DateTimePicker
              label="Date *"
              value={values.date}
              onChange={(value) => setFieldValue('date', value)}
              format="DD.MM.YYYY HH:mm"
            />

            <TextField
              name="description"
              label="Description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError('description')}
              helperText={errorText('description') || `${values.description.length}/${DESCRIPTION_MAX_LENGTH}`}
              slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH } }}
              multiline
              minRows={3}
              maxRows={5}
              fullWidth
              required
            />

            <Select
              label="Type"
              value={values.type}
              options={taskTypeOptions}
              onChange={(value) => setFieldValue('type', value)}
              required
            />

            <CheckBoxAutocomplete
              label="Roles"
              value={values.allowedUserRoles}
              options={USER_ROLES}
              onChange={(value) => setFieldValue('allowedUserRoles', value)}
              required
            />

            <TournamentSelect
              value={values.tournamentId}
              onChange={(value) => {
                setFieldValue('tournamentId', value);
                setFieldValue('gameId', '');
              }}
            />

            <TournamentSlotPicker
              tournamentId={values.tournamentId}
              gameId={values.gameId}
              onChange={(gameId) => setFieldValue('gameId', gameId)}
              error={errorText('gameId')}
              disabled={initialValues.type === DailyTaskType.TOP_X && isEditForm}
            />

            {renderDefaultFields()}

            {isEditForm ? (
              <Tooltip title="Special task checkbox is disabled while editing">
                <FormControlLabel
                  control={<Checkbox name="isSpecial" checked={values.isSpecial} disabled />}
                  label="Special Task"
                />
              </Tooltip>
            ) : (
              <FormControlLabel
                control={<Checkbox name="isSpecial" checked={values.isSpecial} onChange={handleChange} />}
                label="Special Task"
              />
            )}

            {values.isSpecial && (
              <>
                {numberField('completedCountLimit', 'Initial Count Limit')}
                <Select
                  label="Special Task Type"
                  value={values.specialTaskType}
                  options={specialTaskTypeOptions}
                  onChange={(value) => setFieldValue('specialTaskType', value)}
                  disabled={isEditForm}
                  required
                />
                {renderSpecialFields()}
              </>
            )}

            <Typography fontSize="16px" fontWeight={600}>
              Rewards
            </Typography>

            <DailyRewardAvailableTickets rewards={values.rewards} />

            <RewardsFormControl
              formik={formik}
              initialValue={{ ...DEFAULT_REWARD_STUB }}
              required
              withCount={values.isSpecial}
            />

            <FileSelector
              label="Image"
              value={values.imageId ?? ''}
              onSelect={(value) => setFieldValue('imageId', value)}
            />

            <Button type="submit" variant="contained" loading={isSubmitting} disabled={isSubmitting || !dirty}>
              {submitButtonCaption}
            </Button>
          </form>
        );
      }}
    </Formik>
  );
};
